/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/react";
import {
  HallowAnalyticsEvent,
  useAnalytics,
  useViewedScreen,
} from "@packages/analytics";
import type { PhoneAuthFlowForm } from "@packages/sdk";
import { phoneAuthFlowSchema, useRequestOtp } from "@packages/sdk";
import { HallowDataTestId } from "@tools/playwright/global";
import type { ReactNode } from "react";
import { useForm } from "react-hook-form";

import type { ThemeType } from "../../../../global/emotion";
import { spacing } from "../../../../global/emotion";
import { useIsDesktopViewport } from "../../../hooks/useIsDesktopViewport";
import { useValidator } from "../../../hooks/useValidator.emotion";
import { cssMediaQueryMinWidthTablet } from "../../../lib/constants/emotionMediaQueries.constants";
import { standardSchemaResolver } from "../../../reactHookForm";
import { Button } from "../../_base/_core/Button/Button.emotion";
import { Text } from "../../_base/_core/Text/Text.emotion";
import { useAlert } from "../../_base/modals/Dialog/Dialog.emotion";
import { FormField } from "../forms/wrapper/FormField.emotion";
import { AuthError } from "./AuthError.emotion";
import { EmailHeader } from "./AuthFlowHeader.emotion";
import { AuthForm } from "./AuthForm.emotion";
import { AuthPhoneInput } from "./AuthPhoneInput.emotion";
import { AuthSubmitButton } from "./AuthSubmitButton.emotion";
import type { AuthFlowProps } from "./AuthTypes.emotion";

const disclaimerStyle: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.neutralsHigh,
});
const buttonsStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  flexDirection: "column-reverse",
  [cssMediaQueryMinWidthTablet]: {
    flexDirection: "row",
    justifyContent: "space-between",
  },
  gap: spacing.ms,
  justifyContent: "flex-end",
};
const justOneStyle: Interpolation = {
  justifyContent: "flex-end",
};
const footerStyle: Interpolation = {
  flexDirection: "column",
};
const inputFormFieldStyle: Interpolation = {
  width: spacing.full,
};
const cssLegalLink: Interpolation = {
  textDecoration: "underline",
};

const annotationTagPattern =
  /<annotation id="(privacy|terms)">(.*?)<\/annotation>/g;

const renderPhoneDisclaimer = (text: string) => {
  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  annotationTagPattern.lastIndex = 0;
  while ((match = annotationTagPattern.exec(text))) {
    const [fullMatch, id, chunk] = match;
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }
    nodes.push(
      <a
        key={match.index}
        href={
          id === "privacy"
            ? "https://hallow.com/privacy-policy"
            : "https://hallow.com/terms-of-service"
        }
        rel="noreferrer noopener"
        target="_blank"
        css={cssLegalLink}
      >
        {chunk}
      </a>,
    );
    lastIndex = match.index + fullMatch.length;
  }
  nodes.push(text.slice(lastIndex));

  return nodes;
};

export const PhoneAuthFlow = ({
  phone,
  state,
  setState,
  setFlow,
  allowedMethods,
  hideHeader,
  className = "",
}: AuthFlowProps) => {
  const { track } = useAnalytics();
  const t = useTranslations();
  const alert = useAlert();
  const { mutationPost: sendOtp } = useRequestOtp();
  const isDesktop = useIsDesktopViewport();
  const onSubmit = async ({ phone }: PhoneAuthFlowForm) => {
    try {
      const { exists } = await sendOtp.mutateAsync({ phone });
      const newState = { ...state, phone, exists };
      if ("email" in newState) delete newState.email;
      setState(newState);
      setFlow("otp");
    } catch {
      await alert.show({
        title: t("general_word_error"),
        description: t("error_invalid_phone_number"),
      });
    }
  };

  const { resolver, errors, errorsCSS, fieldsCSS, inputProps } =
    useValidator<PhoneAuthFlowForm>(
      standardSchemaResolver(phoneAuthFlowSchema),
    );
  const form = useForm({ resolver });

  const handleUseEmailInstead = () => {
    track({
      event: HallowAnalyticsEvent.TappedUseEmailInstead,
      properties: { screen: "phone_number_input" },
    });
    setFlow("email");
  };

  useViewedScreen({ screen: "phone_number_input" });

  return (
    <AuthForm<PhoneAuthFlowForm>
      form={form}
      defaultValues={{ phone }}
      onSubmit={onSubmit}
      className={className}
      footerCSS={footerStyle}
      footer={(_, disabled) => (
        <>
          <Text size="l" type="detail" css={disclaimerStyle}>
            {renderPhoneDisclaimer(t.raw("general_words_phone_disclaimer"))}
          </Text>
          <div
            key={"buttons"}
            css={[
              buttonsStyle,
              !allowedMethods.includes("email") && justOneStyle,
            ]}
          >
            {allowedMethods.includes("email") && (
              <Button
                type={"button"}
                isFullWidth={!isDesktop}
                size="m"
                variant="transparentNeutral"
                onClick={handleUseEmailInstead}
                data-testid={HallowDataTestId.AuthUseEmailInsteadButton}
              >
                {t("sms_sign_up_email_instead")}
              </Button>
            )}
            <AuthSubmitButton disabled={disabled}>
              {t("verify_via_text_message")}
            </AuthSubmitButton>
          </div>
        </>
      )}
    >
      {(form) => (
        <>
          {!hideHeader && (
            <EmailHeader
              onBack={() => {
                track({
                  event: HallowAnalyticsEvent.TappedBack,
                  properties: { screen_name: "phone_number_input" },
                });
                setFlow("main");
              }}
            />
          )}
          <Text type="headline" size="m" as="h2">
            {t("sms_sign_up_phone_number")}
          </Text>
          <FormField
            control={form.control}
            name="phone"
            defaultValue={""}
            render={({ field }) => (
              <div>
                <AuthPhoneInput
                  required
                  css={fieldsCSS.phone}
                  {...inputProps}
                  {...field}
                />
                <AuthError css={[errorsCSS.phone, { marginTop: spacing.m }]}>
                  {errors?.phone?.message ? t(errors.phone.message) : ""}
                </AuthError>
              </div>
            )}
            css={inputFormFieldStyle}
          />
        </>
      )}
    </AuthForm>
  );
};
